import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const ambientFloatLoopSkill: Skill = {
    name: SKILL_NAMES.ambientFloatLoop,
    kind: 'codeo',
    description:
        'A selective touch of continuous life — a slow float, breathing scale, or lazy rotation — ' +
        'for one or two decorative or background elements in a scene that would otherwise sit ' +
        'completely still. Use sparingly; skip it when an atmosphere or particle layer already carries the motion.',
    instructions: `# Ambient Float Loop

A scene that animates in and then sits completely frozen reads as cheap — but the cure is a *small, selective* touch, not motion on everything. Pick the one or two elements that can carry a little life — a backdrop shape, a decorative mark, a hero graphic — and let everything else hold still. Over-applied, the floats fight each other and the whole frame feels like it's bobbing on water.

**Reach for this only when the scene has no other ambient motion.** If you've already laid in an \`${SKILL_NAMES.atmosphereGradientField}\`, an \`${SKILL_NAMES.ambientParticleField}\`, or drifting brand motifs, the scene is already alive at the background layer — adding foreground floats on top is what makes it feel busy and off. In that case skip this, or limit it to a single hero element.

Two hard rules keep this safe: repeats are FINITE (computed from how long the element is on screen — never an infinite repeat), and periods VARY per element so the floats don't sync into one robotic pulse.

## Markup

Position them with \`left\`/\`top\` only — GSAP owns \`x\`/\`y\`/\`scale\`/\`rotation\` on these elements, so none of those can also appear as a CSS \`transform\`.

\`\`\`html
<div id="sun-disc" style="position: absolute; left: 780px; top: 140px; width: 220px; height: 220px; border-radius: 50%; background: var(--l5-color-1, #7a6dff);"></div>
<div id="halo" style="position: absolute; left: 720px; top: 80px; width: 340px; height: 340px; border-radius: 50%; background: radial-gradient(circle, rgba(122,109,255,0.35) 0%, rgba(122,109,255,0) 70%);"></div>
<div id="cloud" style="position: absolute; left: 1200px; top: 260px; width: 260px; height: 120px; border-radius: 60px; background: var(--l5-color-2, #d7d7e2); opacity: 0.6;"></div>
<div id="ray-wheel" style="position: absolute; left: 1500px; top: 700px; width: 180px; height: 180px; border-radius: 50%; background: conic-gradient(var(--l5-color-1, #7a6dff) 0deg, transparent 40deg, var(--l5-color-1, #7a6dff) 80deg, transparent 120deg); opacity: 0.4;"></div>
\`\`\`

## Timeline code

A helper computes the repeat count from the element's on-screen window, then per-element calls with varied periods, all on the one paused timeline:

\`\`\`js
const tl = gsap.timeline({ paused: true });

function addFloat(target, startT, endT, dy, period) {
    const halfCycles = Math.max(2, Math.round((endT - startT) / (period / 2)));
    tl.to(target, {
        y: \`-=\${dy}\`,
        duration: period / 2,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: halfCycles - 1,
    }, startT);
}

// periods deliberately desynchronized: 2.1 / 2.45 / 2.8
addFloat('#sun-disc', 1.0, 9.0, 10, 2.45);
addFloat('#halo',     1.2, 9.0,  6, 2.1);
addFloat('#cloud',    0.8, 9.0, 14, 2.8);

// Breathing scale variant, layered onto the same halo's float
gsap.set('#halo', { transformOrigin: '50% 50%' });
tl.to('#halo', { scale: 1.02, duration: 1.4, ease: 'sine.inOut', yoyo: true, repeat: 9 }, 1.2);

// Slow rotation for decorative geometry (one-way, no yoyo)
tl.to('#ray-wheel', { rotation: 14, duration: 8, ease: 'none' }, 1.0);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- Count: float one or two elements, three at the very most. A single calm drift reads as "alive"; four or five competing floats read as "unstable".
- Float distance: ±6–10px for mid-size shapes, up to ±14px for large soft background forms. Subtle is the point — at reading distance it should register as "alive", not "bouncing".
- Period: 1.8–3s feels calm; under ~1.2s reads as nervous. Spread periods across elements (e.g. 2.1 / 2.45 / 2.8).
- Start the float AFTER the entrance tween fully lands (entrance position + duration), so the relative \`-=\` offset composes from the resting state.

## Pitfalls

- **Never an infinite repeat** — a finite, computed repeat count is required so the render always completes and the timeline stays seekable.
- Odd \`repeat\` counts (half-cycles − 1 with yoyo) end the loop back at the resting position; ending mid-float leaves the element visibly displaced for its exit tween.
- Float decorative geometry and backdrop shapes — NOT body text, stats, or UI the viewer is reading or scanning. A floating headline is acceptable only at large display sizes with dy ≤ 6.
- The relative \`y: '-='\` composes with whatever y the entrance left behind — if the entrance and the float overlap in time they fight; sequence them.
- These tweens animate \`transform\` via GSAP — the usual rule applies: no \`transform\` in the floated element's CSS. \`gsap.set(..., { transformOrigin })\` is fine (transform-origin is not a transform channel).`,
};
